import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Upload, Trash2, Image as ImageIcon, X, Check, Loader2 } from 'lucide-react';
import api from '../api';

export default function MediaPickerModal({ onSelect, onClose }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const { data: media = [], isLoading } = useQuery({
    queryKey: ['admin-media'],
    queryFn: () => api.get('/admin/media').then((r) => r.data.data),
  });

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    let lastUrl = null;
    let successCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: files.length, name: file.name });

      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await api.post('/admin/media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const url = res.data?.data?.url || res.data?.url;
        if (url) lastUrl = url;
        successCount++;
      } catch (err) {
        toast.error(`Failed to upload "${file.name}": ${err.response?.data?.detail || 'Upload failed'}`);
      }
    }

    await qc.invalidateQueries({ queryKey: ['admin-media'] });

    if (successCount > 0) {
      toast.success(
        files.length === 1
          ? 'File uploaded'
          : `${successCount} of ${files.length} files uploaded`
      );
      if (lastUrl) setSelected(lastUrl);
    }

    setUploading(false);
    setUploadProgress(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this file?')) return;
    setDeleting(id);
    try {
      await api.delete(`/admin/media/${id}`);
      qc.invalidateQueries({ queryKey: ['admin-media'] });
      toast.success('File deleted');
      const deletedItem = media.find((m) => m.id === id);
      if (deletedItem && selected === deletedItem.url) setSelected(null);
    } catch {
      toast.error('Could not delete file');
    } finally {
      setDeleting(null);
    }
  };

  const handleConfirm = () => {
    if (selected) onSelect(selected);
  };

  const uploadLabel = uploadProgress
    ? `Uploading ${uploadProgress.current} / ${uploadProgress.total}…`
    : uploading
    ? 'Uploading…'
    : 'Upload';

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div>
            <h2 className="font-bold text-[#1F2D45] text-lg">Media Library</h2>
            <p className="text-xs text-slate-400 mt-0.5">Select an existing image or upload a new one</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={handleUpload}
              multiple
              className="hidden"
              id="media-picker-upload"
            />
            <label
              htmlFor="media-picker-upload"
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm font-medium cursor-pointer hover:bg-slate-50 transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploadLabel}
            </label>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Upload progress bar */}
        {uploadProgress && uploadProgress.total > 1 && (
          <div className="px-6 py-2 border-b border-slate-100 shrink-0">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span className="truncate max-w-xs">{uploadProgress.name}</span>
              <span>{uploadProgress.current} / {uploadProgress.total}</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-1.5">
              <div
                className="bg-[#C9962E] h-1.5 rounded-full transition-all"
                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : media.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ImageIcon className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-slate-400 text-sm">No files yet.</p>
              <label htmlFor="media-picker-upload" className="mt-2 text-sm text-[#C9962E] cursor-pointer hover:underline">
                Upload your first image
              </label>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {media.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelected(selected === item.url ? null : item.url)}
                  className={`group relative rounded-xl border-2 overflow-hidden transition-all focus:outline-none ${
                    selected === item.url
                      ? 'border-[#C9962E] ring-2 ring-[#C9962E]/30'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="aspect-square bg-slate-100">
                    <img
                      src={item.url}
                      alt={item.original_name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full hidden items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-slate-300" />
                    </div>
                  </div>
                  <p className="px-1.5 py-1 text-[10px] text-slate-500 truncate text-left">{item.original_name}</p>

                  {selected === item.url && (
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-[#C9962E] rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={(e) => handleDelete(e, item.id)}
                    disabled={deleting === item.id}
                    title="Delete"
                    className="absolute top-1.5 left-1.5 w-6 h-6 bg-white/90 rounded-full items-center justify-center text-red-500 hover:bg-red-50 transition-colors hidden group-hover:flex disabled:opacity-50"
                  >
                    {deleting === item.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Trash2 className="w-3 h-3" />
                    )}
                  </button>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 shrink-0">
          <p className="text-sm text-slate-500">
            {selected ? (
              <span className="text-[#1F2D45] font-medium truncate max-w-sm inline-block align-bottom">{selected}</span>
            ) : (
              'No image selected'
            )}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selected}
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#1F2D45] text-white text-sm font-semibold hover:bg-[#2a3d5e] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" /> Use Image
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
